import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormToggle from '../../../components/form/FormToggle';

import { processAllOptionGet } from '../../../store/slices/tenantSlices/dailyReportSlice';
import {
  dispositionListGetByProcess,
  subDispositionAdd,
  subDispositionListGetById,
  subDispositionUpdate
} from '../../../store/slices/tenantSlices/settingSlice';

const AddEditSubDispositionModal = ({
  isOpen,
  onCloseClick,
  isEdit = false,
  editRecord = null
}) => {
  const dispatch = useDispatch();
  const [processOptions, setProcessOptions] = useState([]);
  const [dispOptions, setDispOptions] = useState([]);

  const initialValues = {
    dispocode: '',
    disposition_id: '',
    active: 'true',
    process_id: ''
  };

  const validationSchema = Yup.object({
    dispocode: Yup.string().required('Disposition Code is required'),
    disposition_id: Yup.string().required('Disposition is required'),
    process_id: Yup.string().required('Process is required')
  });

  const onSubmit = async () => {
    if (isEdit) {
      try {
        const payload = { ...values, id: editRecord.id };
        const response = await dispatch(subDispositionUpdate(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Updated Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        const payload = { ...values };
        const response = await dispatch(subDispositionAdd(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Added Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const fetchSubDispoRecord = async () => {
    try {
      const response = await dispatch(subDispositionListGetById(editRecord?.id)).unwrap();
      setValues({
        ...values,
        dispocode: response.SubDisposCode,
        disposition_id: response.DispoId,
        process_id: response.ProcessId,
        active: String(response.active)
      });
    } catch (error) {
      console.log('error', error);
    }
  };

  const getProcessOption = async () => {
    try {
      const response = await dispatch(processAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getProcessOption();
  }, []);

  const getDispOption = async () => {
    try {
      const response = await dispatch(
        dispositionListGetByProcess({ process_id: values?.process_id })
      ).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.DisposCode,
          value: val.id
        }));
        setDispOptions(options);
      } else {
        setDispOptions([]);
      }
    } catch (error) {
      setDispOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (values?.process_id !== '') {
      getDispOption();
    } else {
      setDispOptions([]);
    }
  }, [values?.process_id]);

  useEffect(() => {
    if (isEdit) {
      fetchSubDispoRecord();
    }
  }, [editRecord]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Sub Disposition' : 'Add Sub Disposition'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="crms" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormInput
                name="dispocode"
                label="Disposition Code"
                placeholder="Enter Disposition Code"
                value={values.dispocode}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="active"
                label="Active"
                value={values.active}
                onChange={(e) => setFieldValue('active', e)}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="process_id"
                label="Process"
                placeholder="Select Process"
                value={values.process_id}
                options={processOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                menuPlacement="bottom"
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="disposition_id"
                label="Disposition"
                placeholder="Select Disposition"
                value={values.disposition_id}
                options={dispOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                menuPlacement="bottom"
              />
            </Col>
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="crms" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditSubDispositionModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditSubDispositionModal;
